/**
 * API Result Mapper
 * Wraps HTTP client calls in Result pattern at the API layer
 * HTTP clients throw HttpError, this module catches and converts to AppError
 */

import type { Result } from '../../types/result.interface';
import type { AppError } from '../../types/errors.interface';
import type { HttpResponse } from '../../services/http/types/httpClient.interface';
import { success, failure } from '../../types/result.interface';
import { extractBackendErrors, isBackendResponseSuccess } from '../../utils/backendErrorExtractor';
import { createAppError } from '../../utils/errorCategoryMapper';
import { mapHttpExceptionToAppError } from '../../services/http/utils/httpErrorMapper';

/**
 * Wraps an HTTP call in Result pattern
 * Catches HttpError exceptions and converts to AppError
 * Extracts backend errors from response data
 * 
 * @param httpCall - Function that returns Promise<HttpResponse<T>>
 * @returns Result<T, AppError>
 * 
 * @example
 * return wrapHttpCall(() => this.api.get<UserDTO>('/users/123'));
 */
export async function wrapHttpCall<T>(
  httpCall: () => Promise<HttpResponse<T>>
): Promise<Result<T, AppError>> {
  try {
    const response = await httpCall();
    
    // Check backend response for errors
    const backendErrors = extractBackendErrors(response.data);
    
    if (backendErrors.length > 0) {
      // Backend returned application-level errors
      const firstError = backendErrors[0];
      const appError = createAppError(
        firstError.code,
        firstError.message,
        firstError.field,
        response.status
      );
      return failure(appError);
    }
    
    // Success - return the data
    return success(response.data);
  } catch (error) {
    // Map caught exception (HttpError or network error) to AppError
    const appError = mapHttpExceptionToAppError(error);
    return failure(appError);
  }
}

/** Like wrapHttpCall, but a backend rejection fails with every error it returned, not just the first. */
export async function wrapHttpCallWithAllErrors<T>(
  httpCall: () => Promise<HttpResponse<T>>
): Promise<Result<T, AppError[]>> {
  try {
    const response = await httpCall();
    const backendErrors = extractBackendErrors(response.data);

    if (backendErrors.length > 0) {
      return failure(
        backendErrors.map(e =>
          createAppError(e.code, e.message, e.field, response.status)
        )
      );
    }

    return success(response.data);
  } catch (error) {
    return failure([mapHttpExceptionToAppError(error)]);
  }
}

/**
 * Wraps an HTTP call with explicit success check
 * Use for endpoints that return { success: boolean, ... } structure
 * 
 * @param httpCall - Function that returns Promise<HttpResponse<T>>
 * @returns Result<T, AppError>
 * 
 * @example
 * return wrapHttpCallWithSuccessCheck(() => 
 *   this.api.post<LoginDTO>('/auth/login', credentials)
 * );
 */
export async function wrapHttpCallWithSuccessCheck<T>(
  httpCall: () => Promise<HttpResponse<T>>
): Promise<Result<T, AppError>> {
  try {
    const response = await httpCall();
    
    // Check if backend indicates success
    if (!isBackendResponseSuccess(response.data)) {
      const backendErrors = extractBackendErrors(response.data);
      const firstError = backendErrors[0] || { code: 'UNK_001', message: 'Operation failed' };
      const appError = createAppError(
        firstError.code,
        firstError.message,
        firstError.field,
        response.status
      );
      return failure(appError);
    }
    
    return success(response.data);
  } catch (error) {
    // Map caught exception to AppError
    const appError = mapHttpExceptionToAppError(error);
    return failure(appError);
  }
}

/**
 * Extracts data from a successful Result or returns default value
 * Useful for optional data
 * 
 * @param result - Result to extract from
 * @param defaultValue - Default value if result is failure
 * @returns Data or default value
 */
export function getResultDataOrDefault<T>(result: Result<T, AppError>, defaultValue: T): T {
  return result.success ? result.data : defaultValue;
}

/**
 * Combines multiple Results into a single Result
 * If any Result is a failure, returns the first failure
 * Otherwise returns success with array of all data
 * 
 * @param results - Array of Results to combine
 * @returns Combined Result
 */
export function combineResults<T>(results: Result<T, AppError>[]): Result<T[], AppError> {
  const data: T[] = [];
  
  for (const result of results) {
    if (!result.success) {
      return failure(result.error);
    }
    data.push(result.data);
  }
  
  return success(data);
}
